/**
 * @module Service
 */

import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Order, OrderLineItem, OrderLineItemGroup } from '@feature/orders/shared';
import { BaseHttpService } from '@app/services/base-http.service';

/**
 * Service for managing the order line item within the application.
 *
 * This service provides order line items related functionality, such as fetching,
 * preview, calculation and grouping of line items.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class OrderLineItemService extends BaseHttpService {
	/**
	 * Options for HTTP requests, including headers.
	 * @type {{ headers: HttpHeaders }}
	 * @note Used for the CRUD operations on the user.
	 */
	options: { headers: HttpHeaders } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the OrderLineItemService.
	 *
	 * @constructor
	 * @param {HttpClient} _http - The HttpClient used for making HTTP requests.
	 */
	constructor(private _http: HttpClient) {
		super(_http);
	}

	/**
	 * Gets the preview line items associated with an order that doesn't exist yet.
	 *
	 * @param {Order} order - The order for which to retrieve the preview line items.
	 * @returns {Observable<Order>} - An Observable containing the order with the preview line items.
	 */
	public getPreviewItems(order: Order): Observable<Order> {
		return this._http.post<Order>(`${environment.config.apiUrl}/line-items/preview`, order, this.options);
	}

	/**
	 * Gets the line items associated with an order.
	 *
	 * @param {number} orderId - The ID of the order for which to retrieve the line items.
	 * @returns {Observable<Array<OrderLineItem>>} - An Observable containing the line items.
	 */
	public getLineItems(orderId: number, companyId?: number): Observable<Array<OrderLineItem>> {
		let parameters: HttpParams = this.params();
		if (companyId) parameters = parameters.append('contextCompanyId', companyId);
		return this._http.get<Array<OrderLineItem>>(`${environment.config.apiUrl}/orders/${orderId}/line-items`, { params: parameters });
	}

	/**
	 * Gets the line items associated with an order, grouped by original template group.
	 *
	 * @param {Array<OrderLineItem>} items - An array of order line items to be grouped.
	 * @returns {Array<OrderLineItemGroup>} - An array containing the grouped line items.
	 */
	public groupItems(items: Array<OrderLineItem>): Array<OrderLineItemGroup> {
		const result: Array<OrderLineItemGroup> = [];
		if (items?.length <= 0) return result;

		items.forEach(item => {
			let group = result.find(group => group.alias === item.originalTemplateGroupAlias);
			if (! group) {
				group = new OrderLineItemGroup();
				group.alias = item.originalTemplateGroupAlias;
				group.name = item.originalTemplateGroupName;
				result.push(group);
			}
			group.items.push(item);
		});
		return result;
	}

	/**
	 * Gets the line items ungrouped from an array of grouped line items.
	 *
	 * @param {Array<OrderLineItemGroup>} groups - An array of grouped order line items to be ungrouped.
	 * @returns {Array<OrderLineItem>} - An array containing the line items ungrouped.
	 */
	public ungroupItems(groups: Array<OrderLineItemGroup>): Array<OrderLineItem> {
		const result: Array<OrderLineItem> = [];
		groups.forEach(group => {
			group.items.forEach(item => {
				result.push(item);
			});
		});
		return result;
	}
}
